import PublicNav from "@/components/nav/PublicNav";
import Footer from "@/components/nav/Footer";

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="brand-headline text-2xl md:text-3xl">{title}</h2>
      <div className="mt-4 text-steel leading-relaxed space-y-4 text-[15px]">{children}</div>
    </section>
  );
}

export function Privacy() {
  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <PublicNav />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-24">
        <div className="brand-eyebrow mb-4">LEGAL</div>
        <h1 className="brand-headline text-5xl md:text-6xl">Privacy Policy</h1>
        <p className="text-steel mt-4 text-sm">Last updated: {new Date().toLocaleDateString("en-US", {year:"numeric", month:"long", day:"numeric"})}</p>

        <Section title="Who we are">
          <p>Hired (&quot;we&quot;, &quot;us&quot;) operates the hired.land platform, which provides AI employees to businesses in the United States. This Privacy Policy explains what we collect, how we use it, and the rights you have.</p>
        </Section>

        <Section title="Information we collect">
          <p><span className="text-ivory">Account information:</span> name, business name, work email, phone number, and role you provide during signup and onboarding.</p>
          <p><span className="text-ivory">Business content:</span> tasks, approvals, activity, and any documents or messages you or your AI employees process through the platform.</p>
          <p><span className="text-ivory">Integration data:</span> when you connect a third-party tool (Gmail, calendar, CRM, etc.), we access the data required to perform the tasks you assign — never more than needed.</p>
          <p><span className="text-ivory">Usage & device data:</span> log data, IP address, browser/device metadata, and product analytics needed to operate and secure the service.</p>
          <p><span className="text-ivory">Payment data:</span> handled by our payment processor (Stripe). We do not store full card numbers.</p>
        </Section>

        <Section title="How we use information">
          <p>We use the information to (a) run the service, (b) process AI-generated tasks and approvals you assign, (c) send transactional and service emails, (d) prevent abuse and secure the platform, (e) improve our product, and (f) comply with our legal obligations.</p>
          <p>We do not sell personal data. We do not use your business content to train third-party AI models.</p>
        </Section>

        <Section title="Sub-processors">
          <p>We rely on a small set of sub-processors, including our cloud host, our email provider (Resend), our payments processor (Stripe), and the AI model providers we route through (OpenAI, Anthropic, Google). Contracts require each to protect your data and use it only to provide their service.</p>
        </Section>

        <Section title="Data retention">
          <p>We retain account and business content for as long as your account is active and for a limited period afterward for backup, dispute resolution, and legal compliance. You may request deletion at any time.</p>
        </Section>

        <Section title="Your rights">
          <p>Depending on where you live, you may have the right to access, correct, delete, or export your personal data, and to object to or restrict certain uses. To exercise any of these rights, email <a href="mailto:privacy@hired.land" className="text-gold hover:underline">privacy@hired.land</a>.</p>
          <p>Residents of California, Virginia, Colorado, Connecticut, Utah, and other US states with comprehensive privacy laws have specific rights under those laws, which we honor.</p>
        </Section>

        <Section title="Security">
          <p>We use industry-standard controls to protect your data: encryption in transit, encrypted secrets at rest, role-based access, and detailed audit logs. No system is perfect — please report any security concerns to <a href="mailto:security@hired.land" className="text-gold hover:underline">security@hired.land</a>.</p>
        </Section>

        <Section title="Children">
          <p>Hired is for business use. It is not directed to children under 13, and we do not knowingly collect information from children.</p>
        </Section>

        <Section title="Changes">
          <p>We may update this policy. When we do, we&apos;ll change the &quot;last updated&quot; date and, where required, notify you directly.</p>
        </Section>

        <Section title="Contact">
          <p>Questions? Email <a href="mailto:privacy@hired.land" className="text-gold hover:underline">privacy@hired.land</a>.</p>
        </Section>
      </div>
      <Footer />
    </div>
  );
}

export function Terms() {
  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <PublicNav />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-24">
        <div className="brand-eyebrow mb-4">LEGAL</div>
        <h1 className="brand-headline text-5xl md:text-6xl">Terms of Service</h1>
        <p className="text-steel mt-4 text-sm">Last updated: {new Date().toLocaleDateString("en-US", {year:"numeric", month:"long", day:"numeric"})}</p>

        <Section title="Agreement">
          <p>These Terms govern your access to and use of the Hired platform at hired.land, operated by Hired (&quot;Hired&quot;, &quot;we&quot;, &quot;us&quot;). By creating an account or using the service you agree to these Terms.</p>
        </Section>

        <Section title="Accounts">
          <p>You must be at least 18 years old and authorized to bind your business. You&apos;re responsible for keeping your credentials safe and for everything that happens under your account.</p>
        </Section>

        <Section title="AI Employees & content">
          <p>Hired provides AI employees that automate business tasks such as email, follow-ups, scheduling, and document handling. Outputs are generated by AI models routed through the platform. Outputs may be inaccurate — you remain responsible for reviewing and using them. Use of approval-required tasks is strongly recommended for anything customer-facing, regulatory, or contractual.</p>
          <p>You retain ownership of your business content. You grant Hired the license needed to process it to run the service. We will not use your business content to train third-party AI models.</p>
        </Section>

        <Section title="Acceptable use">
          <p>Don&apos;t use Hired to break the law, harass anyone, send unlawful spam, impersonate others, infringe others&apos; rights, or generate content that violates a third-party&apos;s terms.</p>
        </Section>

        <Section title="Regulated workflows">
          <p>Some workflows — including the Foundation First DigSafe module for Louisiana 811 utility-locate requests — involve regulated processes. Hired helps you prepare and track these requests but does not replace the legal responsibilities of a licensed excavator, contractor, or other regulated party. Louisiana law requires a minimum of two (2) full business days advance notice for locate responses, and as of August 1, 2025, at least one excavator on each job site must complete Louisiana 811 training annually. You are solely responsible for complying with applicable laws.</p>
        </Section>

        <Section title="Trials & billing">
          <p>New customers get a 14-day free trial with defined limits (1 AI employee, 1 tool connection, up to 25 completed tasks). No credit card is required at signup. When the trial ends, we&apos;ll pause your AI employee and preserve your account and completed work until you upgrade to a paid plan.</p>
          <p>Paid plans are billed monthly via Stripe. You can cancel at any time; the plan remains active until the end of the current period.</p>
        </Section>

        <Section title="Termination">
          <p>You may cancel any time from Billing. We may suspend or terminate accounts that violate these Terms or pose a risk to the platform, other customers, or third parties.</p>
        </Section>

        <Section title="Disclaimers">
          <p>The service is provided &quot;as is.&quot; To the fullest extent permitted by law, Hired disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>
        </Section>

        <Section title="Limitation of liability">
          <p>To the fullest extent permitted by law, Hired&apos;s aggregate liability arising out of or relating to the service will not exceed the amount you paid us for the service in the 12 months preceding the claim.</p>
        </Section>

        <Section title="Governing law">
          <p>These Terms are governed by the laws of the State of Delaware, USA, without regard to conflicts-of-law rules. Disputes will be resolved in the state or federal courts located in Delaware.</p>
        </Section>

        <Section title="Contact">
          <p>Questions? Email <a href="mailto:legal@hired.land" className="text-gold hover:underline">legal@hired.land</a>.</p>
        </Section>
      </div>
      <Footer />
    </div>
  );
}
